import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(v || 0);

const EMPTY_FORM = {
  client_id: '', client_name: '', project_id: '', tower: '', floor: '', unit_number: '',
  bhk_type: '', carpet_area: 0, rate_per_sqft: 0, base_price: 0,
  stamp_duty: 0, gst: 0, registration: 0, total_price: 0,
  sale_value: 0, booking_amount: 0, agreement_value: 0,
  booking_date: new Date().toISOString().split('T')[0],
  channel_partner_id: '', is_direct_sir_sale: true, notes: '', status: 'booked'
};

const Sales = ({ apiBase }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  // Master data
  const [projects, setProjects] = useState([]);
  const [masterData, setMasterData] = useState(null);
  const [towers, setTowers] = useState([]);
  const [bhkConfigs, setBhkConfigs] = useState([]);
  const [clients, setClients] = useState([]);
  const [channelPartners, setCPs] = useState([]);

  useEffect(() => {
    fetchSales();
    axios.get(`${apiBase}/projects`).then(r => { setProjects(r.data || []); if (r.data.length > 0) loadProjectMaster(r.data[0].id); }).catch(() => {});
    axios.get(`${apiBase}/clients/list`).then(r => setClients(r.data || [])).catch(() => {});
    axios.get(`${apiBase}/channel-partners`).then(r => setCPs(r.data || [])).catch(() => {});
  }, []);

  const loadProjectMaster = async (projectId) => {
    try {
      const res = await axios.get(`${apiBase}/projects/${projectId}`);
      setMasterData(res.data);
      const twrs = res.data.towers || [];
      setTowers(twrs);
      const allConfigs = [];
      twrs.forEach(t => (t.bhk_configs || []).forEach(c => allConfigs.push({ ...c, tower_id: t.id, tower_name: t.tower_name })));
      setBhkConfigs(allConfigs);
    } catch (e) { console.error(e); }
  };

  const fetchSales = async () => {
    try {
      const res = await axios.get(`${apiBase}/sales`);
      setSales(Array.isArray(res.data) ? res.data : []);
    } catch (e) { console.error(e); setSales([]); }
    finally { setLoading(false); }
  };

  const computePrice = useCallback((data, master) => {
    if (!master) return data;
    const carpet = parseFloat(data.carpet_area) || 0;
    const psf = parseFloat(data.rate_per_sqft) || 0;
    const floor = parseInt(data.floor) || 1;
    const floorRise = master.floor_rise_rate || 200;
    const effectivePSF = psf + (floorRise * (floor - 1));
    const base = carpet * effectivePSF;
    const stampDuty = base * ((master.stamp_duty_rate || 6) / 100);
    const gstAmt = base * ((master.gst_rate || 5) / 100);
    const regFlat = master.registration_flat_amount || 30000;
    const regPct = base * ((master.registration_charge_rate || 1) / 100);
    const reg = Math.max(regPct, regFlat);
    const legal = master.legal_charges || 25000;
    const total = base + stampDuty + gstAmt + reg + legal;
    return { ...data, base_price: Math.round(base), stamp_duty: Math.round(stampDuty), gst: Math.round(gstAmt), registration: Math.round(reg), total_price: Math.round(total), sale_value: Math.round(total), agreement_value: Math.round(total) };
  }, []);

  const handleTowerChange = (towerName) => {
    setFormData(prev => ({ ...prev, tower: towerName, bhk_type: '', floor: '', carpet_area: 0, rate_per_sqft: 0, base_price: 0, total_price: 0 }));
  };

  const handleBHKChange = (bhkType) => {
    const config = bhkConfigs.find(c => c.tower_name === formData.tower && c.bhk_type === bhkType);
    if (config) {
      const updated = { ...formData, bhk_type: bhkType, carpet_area: config.carpet_area || 0, rate_per_sqft: config.price_per_sqft || masterData?.price_per_sqft || 27000 };
      setFormData(computePrice(updated, masterData));
    } else {
      setFormData(prev => ({ ...prev, bhk_type: bhkType }));
    }
  };

  const handleFloorChange = (floor) => {
    const updated = { ...formData, floor };
    setFormData(computePrice(updated, masterData));
  };

  const openNewSale = () => {
    setEditingSale(null);
    const defaultProject = projects.length > 0 ? projects[0].id : '';
    setFormData({ ...EMPTY_FORM, project_id: defaultProject });
    setShowModal(true);
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      client_id: sale.client_id || '', client_name: sale.client_name || '',
      project_id: sale.project_id || '', tower: sale.tower || '', floor: sale.floor || '',
      unit_number: sale.unit_number || '', bhk_type: sale.bhk_type || '',
      carpet_area: sale.carpet_area || 0, rate_per_sqft: sale.rate_per_sqft || 0,
      base_price: sale.base_price || 0, stamp_duty: sale.stamp_duty || 0,
      gst: sale.gst || 0, registration: sale.registration || 0,
      total_price: sale.total_price || sale.sale_value || 0,
      sale_value: sale.sale_value || 0, booking_amount: sale.booking_amount || 0,
      agreement_value: sale.agreement_value || sale.sale_value || 0,
      booking_date: sale.booking_date ? sale.booking_date.split('T')[0] : '',
      channel_partner_id: sale.channel_partner_id || '', is_direct_sir_sale: !!sale.is_direct_sir_sale,
      notes: sale.notes || '', status: sale.status || 'booked'
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, sale_value: parseFloat(formData.sale_value) || parseFloat(formData.total_price) || 0, booking_amount: parseFloat(formData.booking_amount) || 0, created_by: 1 };
      if (editingSale) {
        await axios.put(`${apiBase}/sales/${editingSale.id}`, payload);
      } else {
        await axios.post(`${apiBase}/sales`, payload);
      }
      setShowModal(false);
      setEditingSale(null);
      fetchSales();
    } catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale? Cannot be undone.')) return;
    try { await axios.delete(`${apiBase}/sales/${id}`); fetchSales(); }
    catch (e) { alert('Error: ' + (e.response?.data?.error || e.message)); }
  };

  if (loading) return <div className="loading">Loading sales...</div>;

  const totalValue = sales.reduce((s, x) => s + (x.sale_value || x.total_price || 0), 0);
  const totalBooking = sales.reduce((s, x) => s + (x.booking_amount || 0), 0);

  const towerBHKs = bhkConfigs.filter(c => c.tower_name === formData.tower);
  const uniqueBHKs = [...new Set(towerBHKs.map(c => c.bhk_type))];
  const maxFloors = masterData?.levels || 35;

  return (
    <div>
      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        {[
          { l: 'Total Sales', v: sales.length, c: '#3b82f6', bg: '#eff6ff' },
          { l: 'Total Value', v: `₹${formatCurrency(totalValue)}`, c: '#16a34a', bg: '#f0fdf4' },
          { l: 'Booking Received', v: `₹${formatCurrency(totalBooking)}`, c: '#d97706', bg: '#fffbeb' },
          { l: 'Direct Deals', v: sales.filter(s => s.is_direct_sir_sale).length, c: '#7c3aed', bg: '#f5f3ff' },
        ].map((s, i) => (
          <div key={i} style={{ background: s.bg, border: `1px solid ${s.c}30`, borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: '800', color: s.c }}>{s.v}</div>
            <div style={{ fontSize: '12px', fontWeight: '600', color: s.c, opacity: 0.8 }}>{s.l}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Sales Transactions</h2>
          <button className="btn btn-primary" onClick={openNewSale}>+ New Transaction</button>
        </div>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Deal #</th>
                <th>Client</th>
                <th>Wing / Floor</th>
                <th>BHK</th>
                <th>Carpet Area</th>
                <th>Rate PSF</th>
                <th>Base Price</th>
                <th>Total Value</th>
                <th>Booking Amt</th>
                <th>CP / Type</th>
                <th>Date</th>
                <th>Status</th>
                <th style={{ minWidth: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.length > 0 ? sales.map((s) => (
                <tr key={s.id}>
                  <td><span style={{ fontFamily: 'monospace', fontSize: '11px', color: '#6366f1', fontWeight: 600 }}>{s.deal_number || s.id}</span></td>
                  <td><strong>{s.client_name || '-'}</strong></td>
                  <td>{s.tower || '-'} / {s.floor || '-'}{s.unit_number ? ` - ${s.unit_number}` : ''}</td>
                  <td><span className="badge badge-info">{s.bhk_type || '-'}</span></td>
                  <td style={{ fontFamily: 'monospace' }}>{s.carpet_area ? `${s.carpet_area} sqft` : '-'}</td>
                  <td style={{ fontFamily: 'monospace' }}>₹{formatCurrency(s.rate_per_sqft)}</td>
                  <td style={{ fontWeight: 600 }}>₹{formatCurrency(s.base_price)}</td>
                  <td style={{ fontWeight: 700, color: '#16a34a' }}>₹{formatCurrency(s.sale_value || s.total_price)}</td>
                  <td style={{ color: '#d97706', fontWeight: 600 }}>₹{formatCurrency(s.booking_amount)}</td>
                  <td>
                    {s.channel_partner_id ? (
                      <span className="badge badge-info">{s.channel_partner_name || `CP #${s.channel_partner_id}`}</span>
                    ) : <span className="badge badge-success">Direct</span>}
                  </td>
                  <td style={{ fontSize: '12px' }}>{s.booking_date ? new Date(s.booking_date).toLocaleDateString('en-IN') : s.sale_date ? new Date(s.sale_date).toLocaleDateString('en-IN') : '-'}</td>
                  <td><span className={`badge badge-${s.status === 'booked' || s.status === 'sold' ? 'success' : s.status === 'cancelled' ? 'danger' : 'warning'}`}>{s.status}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button onClick={() => handleEdit(s)} style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #d1d5db', background: '#f8fafc', cursor: 'pointer', fontSize: '11px' }}>✏️</button>
                      <button onClick={() => handleDelete(s.id)} style={{ padding: '3px 8px', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: '11px' }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan="13" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>No sales yet. Click "+ New Transaction" to add one.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingSale ? `Edit Transaction #${editingSale.deal_number || editingSale.id}` : 'New Sale Transaction'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* Client Selection */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Client *</label>
                  <select className="form-input" required value={formData.client_id} onChange={(e) => {
                    const cl = clients.find(c => c.id === parseInt(e.target.value));
                    setFormData({ ...formData, client_id: e.target.value, client_name: cl ? cl.name : '' });
                  }}>
                    <option value="">-- Select Client --</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.name} {c.phone ? `(${c.phone})` : ''}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Booking Date</label>
                  <input type="date" className="form-input" value={formData.booking_date} onChange={(e) => setFormData({ ...formData, booking_date: e.target.value })} />
                </div>
              </div>

              {/* Unit Selection - Master Data Dropdowns */}
              <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', marginTop: '12px' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#166534' }}>📐 Unit Details (from Master Data)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div className="form-group">
                    <label className="form-label">Wing / Tower *</label>
                    <select className="form-input" required value={formData.tower} onChange={(e) => handleTowerChange(e.target.value)}>
                      <option value="">-- Select Wing --</option>
                      {towers.map(t => <option key={t.id} value={t.tower_name}>{t.tower_name}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">BHK Type *</label>
                    <select className="form-input" required value={formData.bhk_type} onChange={(e) => handleBHKChange(e.target.value)}>
                      <option value="">-- Select BHK --</option>
                      {uniqueBHKs.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Floor *</label>
                    <select className="form-input" required value={formData.floor} onChange={(e) => handleFloorChange(e.target.value)}>
                      <option value="">-- Select Floor --</option>
                      {Array.from({ length: maxFloors }, (_, i) => i + 1).map(f => (
                        <option key={f} value={f}>Floor {f}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit Number</label>
                    <input type="text" className="form-input" value={formData.unit_number} onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })} placeholder="e.g. A-1201" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Carpet Area (sqft)</label>
                    <input type="number" step="0.01" className="form-input" value={formData.carpet_area} readOnly style={{ background: '#e2e8f0' }} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Rate PSF (₹)</label>
                    <input type="number" className="form-input" value={formData.rate_per_sqft} readOnly style={{ background: '#e2e8f0' }} />
                  </div>
                </div>
              </div>

              {/* Auto-calculated Price Breakdown */}
              {formData.base_price > 0 && (
                <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', marginTop: '12px' }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#1e40af' }}>💰 Price Breakdown (Auto-calculated)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '8px' }}>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>Base Price</span><div style={{ fontWeight: 700, fontSize: '16px' }}>₹{formatCurrency(formData.base_price)}</div></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>Stamp Duty ({masterData?.stamp_duty_rate || 6}%)</span><div style={{ fontWeight: 600 }}>₹{formatCurrency(formData.stamp_duty)}</div></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>GST ({masterData?.gst_rate || 5}%)</span><div style={{ fontWeight: 600 }}>₹{formatCurrency(formData.gst)}</div></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>Registration</span><div style={{ fontWeight: 600 }}>₹{formatCurrency(formData.registration)}</div></div>
                    <div><span style={{ fontSize: '11px', color: '#64748b' }}>Total Value</span><div style={{ fontWeight: 800, fontSize: '18px', color: '#1e40af' }}>₹{formatCurrency(formData.total_price)}</div></div>
                  </div>
                </div>
              )}

              {/* Payment & Deal Type */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Agreement Value (₹)</label>
                  <input type="number" className="form-input" value={formData.agreement_value || formData.total_price} onChange={(e) => setFormData({ ...formData, agreement_value: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Booking Amount (₹)</label>
                  <input type="number" className="form-input" value={formData.booking_amount} onChange={(e) => setFormData({ ...formData, booking_amount: parseFloat(e.target.value) || 0 })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Deal Type</label>
                  <select className="form-input" value={formData.is_direct_sir_sale ? 'direct' : 'broker'} onChange={(e) => setFormData({ ...formData, is_direct_sir_sale: e.target.value === 'direct', channel_partner_id: e.target.value === 'direct' ? '' : formData.channel_partner_id })}>
                    <option value="direct">Direct Deal</option>
                    <option value="broker">Via Channel Partner</option>
                  </select>
                </div>
                {!formData.is_direct_sir_sale && (
                  <div className="form-group">
                    <label className="form-label">Channel Partner</label>
                    <select className="form-input" value={formData.channel_partner_id} onChange={(e) => setFormData({ ...formData, channel_partner_id: e.target.value })}>
                      <option value="">-- Select CP --</option>
                      {channelPartners.map(cp => <option key={cp.id} value={cp.id}>{cp.name} {cp.company_name ? `(${cp.company_name})` : ''}</option>)}
                    </select>
                  </div>
                )}
                {editingSale && (
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-input" value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                      <option value="booked">Booked</option>
                      <option value="sold">Sold</option>
                      <option value="pending">Pending</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group" style={{ marginTop: '12px' }}>
                <label className="form-label">Notes</label>
                <textarea className="form-input" rows="2" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>{editingSale ? 'Update Transaction' : 'Create Transaction'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;
